import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_EMPTY_VALUE, FilterDef, FilterValue } from './types';

const emptyFor = (def: FilterDef): FilterValue =>
  def.type === 'multiselect' ? [] : (def.emptyValue ?? DEFAULT_EMPTY_VALUE);

function readValue(def: FilterDef, params: URLSearchParams): FilterValue {
  if (def.fromParam) return def.fromParam(params);
  if (def.type === 'multiselect') return params.getAll(def.urlParam);
  if (def.type === 'select') return params.get(def.urlParam) ?? (def.emptyValue ?? DEFAULT_EMPTY_VALUE);
  return params.get(def.urlParam) ?? '';
}

export function isFilterEmpty(def: FilterDef, value: FilterValue): boolean {
  if (def.type === 'multiselect') return !value || (value as string[]).length === 0;
  if (def.type === 'select') return value === undefined || value === (def.emptyValue ?? DEFAULT_EMPTY_VALUE);
  return !value;
}

function paramKeys(def: FilterDef): string[] {
  if (def.toParam) return Object.keys(def.toParam(emptyFor(def)));
  return [def.urlParam];
}

function applyValue(def: FilterDef, value: FilterValue, params: URLSearchParams): void {
  if (def.toParam) {
    Object.entries(def.toParam(value)).forEach(([key, val]) => {
      if (val === null || val === '') params.delete(key);
      else params.set(key, val);
    });
    return;
  }
  if (def.type === 'multiselect') {
    params.delete(def.urlParam);
    (Array.isArray(value) ? value : []).forEach((v) => params.append(def.urlParam, v));
    return;
  }
  if (isFilterEmpty(def, value)) params.delete(def.urlParam);
  else params.set(def.urlParam, value as string);
}

export interface UseTableFiltersResult {
  /** Current value per filter id, derived from the URL. */
  values: Record<string, FilterValue>;
  /** Ids of filters that currently have a non-empty value. */
  activeFilterIds: string[];
  setFilter: (id: string, value: FilterValue) => void;
  clearFilter: (id: string) => void;
  /** Clear all registered filters (does not touch search). */
  clearAll: () => void;
  /** Current `?search` value. */
  searchValue: string;
  /** Write `?search` (callers should debounce input). */
  setSearch: (value: string) => void;
}

/**
 * Centralizes URL <-> filter-state sync for a table. The URL is the single
 * source of truth; every mutation resets `page=1` and preserves unrelated
 * params (e.g. `per_page`). Shared by Contacts, Sessions, and Visitors.
 */
export function useTableFilters(defs: FilterDef[]): UseTableFiltersResult {
  const location = useLocation();
  const navigate = useNavigate();

  const values = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const result: Record<string, FilterValue> = {};
    defs.forEach((def) => {
      result[def.id] = readValue(def, params);
    });
    return result;
  }, [defs, location.search]);

  const commit = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const next = new URLSearchParams(location.search);
      mutate(next);
      next.set('page', '1');
      navigate(`?${next.toString()}`);
    },
    [location.search, navigate],
  );

  const setFilter = useCallback(
    (id: string, value: FilterValue) => {
      const def = defs.find((d) => d.id === id);
      if (!def) return;
      commit((p) => applyValue(def, value, p));
    },
    [defs, commit],
  );

  const clearFilter = useCallback(
    (id: string) => {
      const def = defs.find((d) => d.id === id);
      if (!def) return;
      commit((p) => paramKeys(def).forEach((k) => p.delete(k)));
    },
    [defs, commit],
  );

  const clearAll = useCallback(() => {
    commit((p) => {
      defs.forEach((def) => paramKeys(def).forEach((k) => p.delete(k)));
      p.delete('search');
    });
  }, [defs, commit]);

  const searchValue = useMemo(
    () => new URLSearchParams(location.search).get('search') || '',
    [location.search],
  );

  const setSearch = useCallback(
    (value: string) => {
      commit((p) => {
        if (value) p.set('search', value);
        else p.delete('search');
      });
    },
    [commit],
  );

  const activeFilterIds = useMemo(
    () => defs.filter((def) => !isFilterEmpty(def, values[def.id])).map((def) => def.id),
    [defs, values],
  );

  return { values, activeFilterIds, setFilter, clearFilter, clearAll, searchValue, setSearch };
}
